import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { AuthUser, LoginFormData } from '../types';
import { login, getCurrentUser } from '../services/api';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** Initial session check on app load (route guards only) */
  isInitializing: boolean;
  /** True while login API + user fetch is in progress */
  isLoggingIn: boolean;
  /** @deprecated Use isInitializing — kept for route guards */
  isLoading: boolean;
  login: (data: LoginFormData) => Promise<void>;
  logout: () => void;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check if token exists and is valid on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setIsInitializing(false);
        return;
      }
      
      try {
        // Check token expiration
        const decoded: any = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp < currentTime) {
          // Token expired
          localStorage.removeItem('token');
          setIsInitializing(false);
          return;
        }
        
        // Token valid, get user data
        const userData = await getCurrentUser();
        setUser({
          ...userData,
          tenant_level: decoded.tenant_level || 'L3' // Default to lowest level if not specified
        });
      } catch (err) {
        console.error('Auth error:', err);
        localStorage.removeItem('token');
      } finally {
        setIsInitializing(false);
      }
    };
    
    checkAuth();
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const handleLogin = async (data: LoginFormData) => {
    setIsLoggingIn(true);
    setError(null);
    
    try {
      const response = await login(data);
      localStorage.setItem('token', response.access_token ?? response.token);
      
      // Decode token to get user info
      const decoded: any = jwtDecode(response.access_token ?? response.token);
      
      // Get full user data
      const userData = await getCurrentUser();
      
      setUser({
        ...userData,
        tenant_level: decoded.tenant_level || 'L3'
      });
      
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isInitializing,
    isLoggingIn,
    isLoading: isInitializing,
    login: handleLogin,
    logout: handleLogout,
    error,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 